import type { ProcessForView } from '@bliks-io/ui';
import { useProcess } from '@/api/queries/useProcesses';
import { useAssigneeLookupWithReadiness } from '@/lib/useAssigneeLookup';
import { useProcessEntitySuggestions } from '@/lib/useProcessEntitySuggestions';
import { isNotFoundError } from '@/api/errors';

/**
 * One process, resolved for a surface that only DRAWS it — with the org lookup
 * and the process-wide suggestion set the product's own components expect
 * beside it, and the cold-cache gate that keeps them from flashing raw ids.
 *
 * It exists because two chat views now need exactly this: the step views find
 * one recording in the response, the flow view hands the whole thing to the
 * canvas. Everything before that point — which query, which readiness gate,
 * and which of the three non-ready states a failure is — is identical, and a
 * second copy would be a second chance to answer one of them differently.
 */

/**
 * The shape lives in `@bliks-io/ui` beside the chat renderers that consume it —
 * `ProcessForView` and its non-ready `ViewFetchState` are what the shared
 * `ChatRendererDataAdapter` seam is typed on, and one declaration is what keeps
 * this hook assignable to it.
 */

export interface ProcessForViewOptions {
  /**
   * Fetch the org-wide assignee catalogs alongside the process (default true).
   *
   * Turn it OFF for a viewer who may not read them — the lists would only
   * 403 — or for a surface that is not showing anything yet. The view then
   * resolves WITHOUT waiting for them and refs render as raw ids: a gate that
   * can never open would hold the surface on a spinner forever.
   */
  withAssigneeLookup?: boolean;
}

export function useProcessForView(
  processId: string,
  { withAssigneeLookup = true }: ProcessForViewOptions = {}
): ProcessForView {
  const { data, isPending, isError, error, refetch } = useProcess(processId);
  const { lookup: assigneeLookup, ready: lookupReady } = useAssigneeLookupWithReadiness({
    enabled: withAssigneeLookup
  });
  const entitySuggestions = useProcessEntitySuggestions(data?.process, data?.entitySuggestions);

  if (isError) {
    return isNotFoundError(error)
      ? { status: 'unavailable' }
      : { status: 'failed', retry: () => void refetch() };
  }

  // `useAssigneeLookup` fans out to four org-wide lists; without the gate the
  // first render resolves nothing and shows raw ids and "Unassigned", then
  // flips. Every consumer here needs all four, so the conjunction is right —
  // unlike an entity card, which reads exactly one map.
  if (isPending || (withAssigneeLookup && !lookupReady)) return { status: 'pending' };

  return { status: 'ready', data, assigneeLookup, entitySuggestions };
}
